import React from 'react';
import PropTypes from 'prop-types';
import { createFragmentContainer, graphql } from 'react-relay';

import get from 'lodash.get';
import { HpRespCarouselTracking } from '../HpRespCarouselTracking/HpRespCarouselTracking';
import { Carousel } from 'dibs-carousel';
import { SharedCarouselDot } from '../../sharedComponents/SharedCarouselDot/SharedCarouselDot';

import { HpRespModuleContainer } from '../HpRespModuleContainer/HpRespModuleContainer';
import { SharedInViewport } from '../../sharedComponents/SharedInViewport';
import { useUID } from 'dibs-uid';

import styles from './HpRespTopHeroBannerModule.scss';

export const HpRespTopHeroBannerModuleComponent = props => {
    const topHeroBannerModule = get(props, 'homepage.topHeroBannerModule');
    const uid = `topHeroBanner-${useUID() || undefined}`;

    if (!topHeroBannerModule) {
        return null;
    }

    const { items = [] } = topHeroBannerModule;

    const itemsPerPage = 1;
    return (
        <div className={styles.carouselContainer}>
            <HpRespModuleContainer>
                <HpRespCarouselTracking items={items} itemsPerPage={itemsPerPage}>
                    {({ handleIndexChange }) => (
                        <SharedInViewport>
                            {({ inViewport }) => (
                                <Carousel
                                    isAutoRun={inViewport && items.length > 1}
                                    dataTn="hero-items-carousel"
                                    showDots={items.length > 1}
                                    hideArrows
                                    totalItems={items.length}
                                    itemsPerPage={itemsPerPage}
                                    step={itemsPerPage}
                                    renderItem={({ index, isVisible }) => {
                                        const {
                                            cmsId,
                                            linkData,
                                            imageUrl,
                                            mobileImageUrl,
                                            cta,
                                            imageCopy,
                                        } = items[index];
                                        // show banner
                                        const itemId = `${uid}-${index}`;
                                        return (
                                            <>
                                                {imageCopy && (
                                                    <span id={itemId} className={styles.imageCopy}>
                                                        {imageCopy}
                                                    </span>
                                                )}
                                                <a
                                                    key={cmsId}
                                                    aria-label={cta}
                                                    aria-describedby={
                                                        imageCopy ? itemId : undefined
                                                    }
                                                    href={linkData?.path}
                                                    className={styles.bannerImgContainer}
                                                    style={{ opacity: isVisible ? '1' : '0' }}
                                                >
                                                    <img
                                                        // do not hide image if there is no copy bc automated a11y testing won't
                                                        // be able to detect this issue
                                                        aria-hidden={imageCopy ? true : false}
                                                        className={styles.bannerImg}
                                                        src={imageUrl || mobileImageUrl}
                                                    />
                                                </a>
                                            </>
                                        );
                                    }}
                                    onIndexChange={handleIndexChange}
                                    renderDot={({ isCurrentDot } = {}) => (
                                        <SharedCarouselDot isActive={isCurrentDot} />
                                    )}
                                />
                            )}
                        </SharedInViewport>
                    )}
                </HpRespCarouselTracking>
            </HpRespModuleContainer>
        </div>
    );
};

HpRespTopHeroBannerModuleComponent.propTypes = {
    homepage: PropTypes.object,
    pageSetOffset: PropTypes.bool,
};

export const HpRespTopHeroBannerModule = createFragmentContainer(
    HpRespTopHeroBannerModuleComponent,
    {
        homepage: graphql`
            fragment HpRespTopHeroBannerModule_homepage on Homepage {
                topHeroBannerModule: module(type: "topHeroBanner") {
                    ... on TopHeroBannerModule {
                        title
                        items {
                            cmsId
                            imageUrl @skip(if: $isMobile)
                            mobileImageUrl @include(if: $isMobile)
                            linkData {
                                path
                            }
                            imageCopy
                            cta
                        }
                    }
                }
            }
        `,
    }
);
